<template>
  <div class="relative h-56 bg-surface-inverse" data-testid="visitor-details-cover">
    <img src="@/assets/images/profiles/cover-stars-right.svg" class="absolute top-10 right-52 h-20" />
    <img src="@/assets/images/profiles/cover-stars-left.svg" class="absolute top-10 left-4 h-24" />
    <img src="@/assets/images/profiles/cover-planets-left.svg" class="absolute top-0 left-20 w-40" />
    <img src="@/assets/images/profiles/cover-planets-right.svg" class="absolute top-10 right-10 w-40" />
  </div>

  <div class="w-full bg-surface-inverse pb-10">
    <div class="grid grid-cols-12 items-stretch gap-4">
      <div class="relative col-span-2">
        <img
          :src="visitor.profileImage || `@/assets/images/profiles/cover-planets-left.svg`"
          alt="Profile"
          class="absolute -top-12 left-1/2 h-28 w-28 -translate-x-1/2 transform rounded-full"
        />
      </div>

      <div class="col-span-5 flex flex-col justify-start text-inverse" data-testid="visitor-details-info">
        <h2 class="text-headline-m uppercase">{{ visitor.name }}</h2>
        <p class="mt-2 text-body-m-bold text-disabled">{{ visitor.companyName }} | {{ visitor.position }}</p>
        <div class="mt-2">
          <ChipBadge v-for="interest in visitor.interests" :key="interest.slug" rounded type="outlined" color="white" class="mt-2 mr-2">
            <span class="text-body-s-bold">{{ interest.text }}</span>
          </ChipBadge>
        </div>
      </div>

      <div class="col-span-5 mr-10 flex justify-end" data-testid="visitor-details-socials">
        <div v-for="socialMediaLink in socialMediaLinks" :key="socialMediaLink.url">
          <a :href="socialMediaLink.url" class="cursor-pointer opacity-20 hover:opacity-100">
            <SvgIcon :path="socialMediaLink.iconPath" class="mr-2 mb-0.5 text-inverse" :size="24" />
          </a>
        </div>
      </div>
    </div>
  </div>

  <div class="grid grid-cols-12 gap-4">
    <div class="relative col-span-1"></div>

    <div class="col-span-5 flex flex-col justify-start" data-testid="visitor-details-content">
      <div class="mt-12 grid grid-cols-2 gap-y-4 border-b border-b-main pb-5 txt-body-m-regular">
        <div
          v-for="(item, index) in visibleVisitorFeatures"
          :key="index"
          class="mt-2 border-t border-t-main pt-2"
          :class="{
            'col-span-2': visibleVisitorFeatures.length % 2 !== 0 && index === visibleVisitorFeatures.length - 1,
            'pl-2': (index + 1) % 2 === 0,
          }"
        >
          <span class="text-gray-500 mr-1">{{ $t(`VisitorProfiles.profileDetails.features.${item.label}`) }}</span>
          <span class="txt-body-m-medium">{{ item.value }}</span>
        </div>
      </div>

      <template v-if="hierarchicalNetworkingInterests.length || flatNetworkingInterests.length">
        <p class="mt-6 mb-2 text-headline-m">{{ $t('VisitorProfiles.profileDetails.imLookingFor') }}</p>
        <div>
          <ChipBadge
            v-for="interest in flatNetworkingInterests"
            :key="interest.id"
            rounded
            type="outlined"
            color="default"
            class="mt-2 mr-2"
          >
            <span class="text-body-s-bold">{{ interest.networkingInterest.title }}</span>
          </ChipBadge>
        </div>

        <div class="mt-4">
          <div
            v-for="(interest, index) in hierarchicalNetworkingInterests"
            :key="index"
            class="mb-4 rounded-lg border border-main bg-surface-elevation-2 p-2"
          >
            <b>{{ interest.networkingInterest.title }}</b>
            <hr v-if="interest.networkingInterest.children.length" class="dark:bg-gray-700 my-2 h-px border-0 bg-surface-elevation-5" />
            <div v-for="(childInterest, childIndex) in interest.networkingInterest.children" :key="childIndex">
              {{ childInterest.title }}
              <hr
                v-if="childIndex !== interest.networkingInterest.children.length - 1"
                class="dark:bg-gray-700 my-2 h-px border-0 bg-surface-elevation-5"
              />
            </div>
          </div>
        </div>
      </template>
    </div>

    <div class="col-span-6 mr-10 flex justify-end">
      <VisitorProfileContactForm :visitor="visitor" class="-mt-10 mr-8 ml-auto h-[580px] w-[350px] rounded-lg shadow-lg" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { mdiYoutube, mdiWeb, mdiInstagram, mdiLinkedin } from '@mdi/js'
import { ChipBadge, iconTwitterX } from '@ramp106/omrjs-core-ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import type { VisitorProfile } from './types'
import VisitorProfileContactForm from './VisitorProfileContactForm.vue'

const { t } = useI18n()

const props = defineProps<{
  visitor: VisitorProfile
}>()

// Add type definition
type VisitorFeature = {
  visible: boolean
  label: string
  value: string | string[]
}

const visitorFeatures = computed<VisitorFeature[]>(() => [
  { visible: false, label: 'attends', value: `OMR Festival 25 on ${props.visitor.attendsFestivalAt}` },
  {
    visible: !!(props.visitor.city && props.visitor.country),
    label: 'livesIn',
    value: `${props.visitor.country}, ${props.visitor.city}`,
  },
  { visible: false, label: 'speaks', value: props.visitor.languages },
  { visible: !!props.visitor.companyJobLevel, label: 'jobLevel', value: `${props.visitor.companyJobLevel}` },
  { visible: !!props.visitor.companySize, label: 'companySize', value: t(`VisitorProfiles.companySizes.${props.visitor.companySize}`) },
  { visible: !!props.visitor.companyDepartment, label: 'department', value: `${props.visitor.companyDepartment}` },
  { visible: !!props.visitor.companyIndustryName, label: 'industry', value: `${props.visitor.companyIndustryName}` },
  { visible: !!props.visitor.companyFieldName, label: 'sector', value: `${props.visitor.companyFieldName}` },
])

const visibleVisitorFeatures = computed(() => visitorFeatures.value.filter((feature) => feature.visible))

const socialMediaLinks = computed(
  () =>
    [
      { url: props.visitor.networkingProfileWebsiteUrl, iconPath: mdiWeb },
      { url: props.visitor.networkingProfileLinkedinUrl, iconPath: mdiLinkedin },
      { url: props.visitor.networkingProfileInstagramUrl, iconPath: mdiInstagram },
      { url: props.visitor.networkingProfileTiktokUrl, iconPath: iconTwitterX },
      { url: props.visitor.networkingProfileYoutubeUrl, iconPath: mdiYoutube },
    ].filter((link) => !!link.url) as { url: string; iconPath: string }[],
)

const flatNetworkingInterests = computed(() =>
  props.visitor.userNetworkingInterests.filter((item) => item.networkingInterest.section === 'flat'),
)

const hierarchicalNetworkingInterests = computed(() =>
  props.visitor.userNetworkingInterests.filter((item) => item.networkingInterest.section === 'hierarchical'),
)
</script>
